import {
  BadgeCheck,
  CalendarDays,
  FileArchive,
  Package2,
  ShieldAlert,
  ShieldCheck,
  Tag,
} from "lucide-react";

export type ClaimStatus = "Open" | "Closed";
export type WarrantyCoverageStatus = "Active" | "Expired";

export type WarrantyClaim = {
  claimDate: string;
  status: ClaimStatus;
  issueDescription: string;
  resolutionNotes: string;
  attachmentName: string;
  attachmentSize: string;
};

export type WarrantyProduct = {
  id: string;
  name: string;
  brand: string;
  warrantyStatus: WarrantyCoverageStatus;
  expiryDate: string;
  price: number;
  originalPrice: number;
  claim?: WarrantyClaim;
};

import { formatAppDate } from "../../utils/dateFormatter";

export const formatWarrantyDate = (value: string) => {
  return formatAppDate(value);
};

export const formatWarrantyCurrency = (value: number) =>
  new Intl.NumberFormat("en-IN", { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(value);

export const getClaimStatusClasses = (status: ClaimStatus) => {
  if (status === "Closed") {
    return "border-[#FDE7C7] bg-[#FFF7ED] text-[#C2410C]";
  }

  return "border-[#DBEAFE] bg-[#EFF6FF] text-[#1D4ED8]";
};

export const getCoverageStatusClasses = (status: WarrantyCoverageStatus) => {
  if (status === "Expired") {
    return "text-[#F15B6C]";
  }

  return "text-[#1A5EF9]";
};

export function WarrantyProductArtwork() {
  return (
    <div className="flex h-28 w-28 items-center justify-center rounded-[22px] border border-[#DDE7FB] bg-[radial-gradient(circle_at_top,rgba(26,94,249,0.16),transparent_45%),linear-gradient(180deg,#F8FAFC_0%,#EEF4FF_100%)] shadow-inner">
      <div className="flex h-20 w-14 items-center justify-center rounded-[18px] bg-[linear-gradient(180deg,#0F4FD6_0%,#1A5EF9_60%,#3A87FF_100%)] shadow-[inset_0_2px_8px_rgba(255,255,255,0.12),0_14px_24px_-18px_rgba(26,94,249,0.55)]">
        <div className="flex h-12 w-9 items-center justify-center rounded-[12px] border border-white/20 bg-[#0C46C9]/30">
          <Package2 className="h-5 w-5 text-white" />
        </div>
      </div>
    </div>
  );
}

export function WarrantyProductSummary({
  product,
}: {
  product: WarrantyProduct;
}) {
  return (
    <div className="space-y-2">
      <h3 className="text-lg font-semibold tracking-tight text-slate-900">
        {product.name}
      </h3>

      <p className="flex items-center gap-2 text-sm text-slate-500">
        {product.warrantyStatus === "Active" ? (
          <ShieldCheck className="h-4 w-4 text-[#1A5EF9]" />
        ) : (
          <ShieldAlert className="h-4 w-4 text-[#F15B6C]" />
        )}
        Warranty Status:
        <span className={getCoverageStatusClasses(product.warrantyStatus)}>
          {product.warrantyStatus}
        </span>
      </p>
      <p className="flex items-center gap-2 text-sm text-slate-500">
        <CalendarDays className="h-4 w-4 text-slate-400" />
        Expiry Date: {formatWarrantyDate(product.expiryDate)}
      </p>
      <p className="flex items-center gap-2 text-sm text-slate-500">
        <Tag className="h-4 w-4 text-slate-400" />
        OMR {formatWarrantyCurrency(product.price)}
        <span className="line-through text-slate-400">
          OMR {formatWarrantyCurrency(product.originalPrice)}
        </span>
      </p>
    </div>
  );
}

export function WarrantyAttachmentChip({
  attachmentName,
  attachmentSize,
}: {
  attachmentName: string;
  attachmentSize: string;
}) {
  return (
    <div className="inline-flex items-center gap-3 rounded-xl border border-[#E8EDF3] bg-white px-3 py-2 shadow-[0_8px_18px_-18px_rgba(15,23,42,0.8)]">
      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#EEF4FF] text-[#1A5EF9]">
        <FileArchive className="h-4 w-4" />
      </div>
      <div className="text-left">
        <p className="text-sm font-medium text-slate-700">{attachmentName}</p>
        <p className="text-xs text-slate-400">{attachmentSize}</p>
      </div>
    </div>
  );
}
